import { Component, ElementRef, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { ENVIRONMENT } from 'environments-lib';
import { Namespace } from 'enums-lib';
import { FeatureFlagService } from 'feature-flags-lib';
import { IEnvironment } from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { catchError, tap } from 'rxjs/operators';
import { Identity } from 'storage-service-lib';
import { IAuthService } from '../../services/auth/iauthservice';
import { ServiceProviderService } from '../../services/service.provider.service';
import { AsyncResult } from '../../utils/asyncresult';
import { LoginService, VerifyLoginOtpResponse } from './login.service';

@Component({
  selector: 'app-login-tenant',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  @Output()
  loginSuccess = new EventEmitter<Identity>();

  @ViewChild('loginOtp')
  loginOtpInput!: ElementRef;

  readonly showPasswordSignIn = this.featureFlagService.enabled(FeatureFlagService.FLAGS.wordAddinPasswordSignIn);
  readonly managerUrl = this.environment.managerUrl ?? '';

  loginSpinner = {
    [Namespace.bullhorn]: false,
    [Namespace.invenias]: false,
    [Namespace.salesforce]: false,
  };

  lastUsedTenant = '';
  showClearIcon = false;
  useSalesforceSandbox = false;

  private userLoginOtp = '';

  constructor(
    private messageNotificationService: MessageNotificationService,
    private serviceProviderService: ServiceProviderService,
    private featureFlagService: FeatureFlagService,
    private service: LoginService,
    @Inject(ENVIRONMENT) private environment: Partial<IEnvironment>
  ) {}

  ngOnInit(): void {
    this.lastUsedTenant = localStorage.getItem('lastUsedTenant') || '';
    this.featureFlagService.init({ environment: this.environment.name ?? '', tenant_id: '' }).subscribe();
  }

  onInput(value: string): void {
    this.showClearIcon = value.length > 0;
  }

  clearInput(input: HTMLInputElement, event?: Event): void {
    event?.preventDefault();
    this.showClearIcon = false;
    input.value = '';
    input.focus();
  }

  signIn(namespace: Namespace, tenantName: string, options?: { env?: string }) {
    const authService = this.serviceProviderService.getAuthService(namespace);
    this.loginSpinner[namespace] = true;

    authService
      .signIn(tenantName, options)
      .pipe(
        tap((response) => {
          const { launchUrl, buildIdentity, successCallback } = response;
          const hideSpinner = () => {
            this.loginSpinner[namespace] = false;
          };
          this.showLoginDialog(launchUrl, authService, buildIdentity, successCallback, hideSpinner);
        }),
        catchError((err) => {
          console.error('Failed to get authentication host.', err);
          this.messageNotificationService.error(`Failed to get authentication host for '${tenantName}'.`);
          this.loginSpinner[namespace] = false;
          return err;
        })
      )
      .subscribe();
  }

  signInWithBullhorn(): void {
    this.signIn(Namespace.bullhorn, '');
  }
  signInWithSalesforce(): void {
    const options = this.useSalesforceSandbox ? { env: 'sandbox' } : undefined;
    this.signIn(Namespace.salesforce, '', options);
  }

  signInWithInvenias(tenantName: string): void {
    this.signIn(Namespace.invenias, tenantName);
  }

  signInWithOtp(): void {
    const otp = this.userLoginOtp;
    if (!otp) {
      this.messageNotificationService.error('Please enter the login code.');
      return;
    }

    this.service.verifyLoginOtp(otp).subscribe((otpObject: VerifyLoginOtpResponse) => {
      if (otpObject?.jwtToken) {
        this.service.signInWithLoginOtp(otpObject, this.loginSuccess);
      }
    });
  }

  onPasteLoginOtp(event: ClipboardEvent, input: HTMLInputElement): void {
    event.preventDefault();
    this.userLoginOtp = event.clipboardData?.getData('text/plain') ?? '';
    input.value = '****************';
  }

  pasteOtpAndLogin(event?: Event) {
    event?.preventDefault();
    this.tryPasteOtp();
    if (this.userLoginOtp) {
      this.signInWithOtp();
    }
  }

  private tryPasteOtp() {
    this.loginOtpInput.nativeElement.value = '';
    this.loginOtpInput.nativeElement.focus();
    try {
      navigator.clipboard
        .readText()
        .then((text) => {
          this.userLoginOtp = text;
          this.loginOtpInput.nativeElement.value = '****************';
        })
        .catch((err) => {
          this.handleClipboardAccessDenied(err);
        });
    } catch (err) {
      this.handleClipboardAccessDenied(err);
    }
  }

  private handleClipboardAccessDenied(err: any) {
    console.error(err);
    this.messageNotificationService.error(
      'Failed to read clipboard contents. Please enter the login code manually, or enable clipboard access from the browser.'
    );
  }

  private showLoginDialog(
    launchUrl: string,
    authService: IAuthService,
    buildIdentity: (jwt: string) => Identity,
    successCallback: () => void,
    hideSpinner: () => void
  ): void {
    Office.context.ui.displayDialogAsync(launchUrl, { width: 40, height: 60 }, (result) => {
      if (AsyncResult.succeeded(result)) {
        const dialog = result.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (args: any) => {
          dialog.close();
          this.handleMessageReceived(args, authService, buildIdentity, successCallback, hideSpinner);
        });
        dialog.addEventHandler(Office.EventType.DialogEventReceived, (args: any) => {
          this.handleEventReceived(args, hideSpinner);
        });
      } else {
        console.error('Could not open login window.', result.error);
        hideSpinner();
        this.messageNotificationService.error('Could not open login window.', result.error);
      }
    });
  }

  private handleEventReceived(args: any, hideSpinner: () => void) {
    // for DialogEventReceived args properties are {type, error}
    console.log('DialogEvent', args);
    if (args.error === 12006) {
      console.log('Dialog closed.');
      hideSpinner();
      this.messageNotificationService.info('You failed to log in. Please try again.');
    }
  }

  private handleMessageReceived(
    args: any,
    authService: IAuthService,
    buildIdentity: (jwt: string) => Identity,
    successCallback: () => void,
    hideSpinner: () => void
  ) {
    // for DialogMessageReceived args properties are {type, message, origin}
    // type = dialogMessageReceived
    // origin = https://localhost:3000
    // message = https://localhost:3000/redirectPage.html?code=<token>

    const url = new URL(args.message);
    const errorJson = url.searchParams.get('error');
    if (errorJson) {
      this.onError(errorJson, hideSpinner);
      return;
    }
    this.onSuccess(args, authService, buildIdentity, successCallback, hideSpinner);
  }

  private onSuccess(
    args: any,
    authService: IAuthService,
    buildIdentity: (jwt: string) => Identity,
    successCallback: () => void,
    hideSpinner: () => void
  ) {
    authService.getToken(args.message).subscribe({
      next: (jwt) => {
        this.loginSuccess.emit(buildIdentity(jwt));
        hideSpinner();
        successCallback();
      },
      error: (err) => {
        hideSpinner();
        this.messageNotificationService.error('Error getting token.', err);
      },
    });
  }

  private onError(errorJson: string, hideSpinner: () => void) {
    hideSpinner();
    const errorObj = JSON.parse(errorJson);
    console.error(errorObj);
    this.messageNotificationService.info(
      `An error occurred. ${errorObj.status ?? '-1'}: ${errorObj.message ?? 'Unknown'}. Please try again.`
    );
  }
}
